import { minMax } from '@tiptap/core';
import type { EditorView } from '@tiptap/pm/view';
import { useContext, useEffect, useState } from 'react';

import { PluginsEventsContext } from './../../../ricos-context';
import type { TiptapEditor } from './../../../ricos-types';

/**
 * Same as import { posToDOMRect } from '@tiptap/core'; except we only use one position property (no need to -1);
 */
function posToDOMRect(view: EditorView, pos: number): DOMRect {
  const minPos = 0;
  const maxPos = view.state.doc.content.size;
  const resolvedFrom = minMax(pos, minPos, maxPos);
  const side = view.state.selection.anchor - view.state.selection.head;
  const start = view.coordsAtPos(resolvedFrom, side);
  const top = start.top;
  const bottom = start.bottom;
  const left = start.left;
  const right = start.right;
  const width = right - left;
  const height = bottom - top;
  const x = left;
  const y = top;
  const data = {
    top,
    bottom,
    left,
    right,
    width,
    height,
    x,
    y,
  };

  return {
    ...data,
    toJSON: () => data,
  };
}

export function useSelectionToPosition(editor: TiptapEditor) {
  const [position, setPosition] = useState<DOMRect>();
  const pluginsEvents = useContext(PluginsEventsContext);

  const handleButtonPosition = () => {
    const { selection } = editor.state;
    const currentPosition = posToDOMRect(editor.view, selection.head);

    const node = editor.view.domAtPos(selection.head)?.node;
    const element = (node && node.nodeType === 3 ? node.parentElement : node) as HTMLElement;
    const container = element ? element.closest('[contenteditable=true]') : editor.view.dom;
    const containerRect = container?.getBoundingClientRect() || { top: 0 };

    setPosition(
      new DOMRect(
        currentPosition.left,
        currentPosition.top - containerRect.top,
        currentPosition.width,
        currentPosition.height
      )
    );
  };

  useEffect(() => {
    handleButtonPosition();
    editor.on('transaction', handleButtonPosition);

    const subscription = pluginsEvents.onPluginLayoutShift(() => {
      handleButtonPosition();
    });

    return () => {
      editor.off('transaction', handleButtonPosition);
      subscription.cancel();
    };
  }, []);

  return position;
}
